/**
 * Platform tech card (accordion) — the API, in one place.
 *
 *   GET  /api/v1/tech-cards/{tech_id}   the card payload for this viewer
 *   GET  /api/v1/ui-config              {tech_card_design: accordion|legacy}
 *   POST /api/v1/share-links            {code, url} — one code per sharer per target
 *
 * The card payload is what the public tech page shows (same sections, same
 * locking, from the same admin matrix), fetched by id so a card needs no
 * page path. The server decides the viewer's state from the bearer token; a
 * locked section arrives without its data.
 *
 * Kept out of services/apiService.ts for the same reason as the tech-page and
 * IP-Intel clients: the bearer is optional and the shapes are this feature's.
 */

import { apiService } from '../../services/apiService';
import { currentIds } from '../../utils/visitorTracking';

// ---------------------------------------------------------------------------
// Shapes (contract: "Tech card payload")
// ---------------------------------------------------------------------------

export type CardState = 'pre_login' | 'post_login' | 'paid';
export type TechCardDesign = 'accordion' | 'legacy';

export type TrlCategory =
  | 'disclosed_confirmed' | 'disclosed_mismatch' | 'disclosed_no_evidence'
  | 'inferred' | 'not_disclosed_no_evidence';

/** tech_pages.trl_block(dev_stage). */
export interface TrlBlock {
  level: number | null;
  /** "TRL 6" | "TRL —" */
  pill: string;
  /** "TRL 6" | "TRL not found" */
  title: string;
  description?: string | null;
  /** The provenance line ("TRL value as disclosed by TTO", …). */
  note?: string | null;
  category?: TrlCategory | string | null;
}

export interface CardSectionLock { reason: 'sign_in' | 'upgrade'; message?: string }

export interface CardSection {
  id: string;
  mode: 'visible' | 'locked' | 'hidden' | 'short' | 'full';
  lock?: CardSectionLock;
  data?: any;
}

export interface CardUniversity {
  code?: string;
  name?: string;
  label?: string | null;
  country?: string | null;
  path?: string | null;
}

export interface TechCardPayload {
  state: CardState;
  id: string;
  title: string;
  university: CardUniversity;
  tech_page_path?: string | null;
  trl: TrlBlock;
  relevance?: number | null;
  sections: CardSection[];
  ctas: { contact_university: boolean; ip_assessment: boolean; share: boolean; bookmark: boolean };
  bookmarked?: boolean;
  ui?: { card_design?: TechCardDesign };
}

export interface SimilarCardItem { id?: string; title: string; path?: string | null; university_label?: string | null }

/** What a list already knows about a tech before its card payload arrives. */
export interface CardPreview {
  title?: string;
  university?: string;
  country?: string;
  /** Search metadata's calculated_trl (number or "6"). */
  trl?: number | string | null;
  summary?: string;
}

// ---------------------------------------------------------------------------
// Session helpers
// ---------------------------------------------------------------------------

const API = (): string => apiService.getApiBaseUrl();

export const storedUserId = (): string => {
  try { return apiService.getStoredUserId?.() || ''; } catch { return ''; }
};
const storedToken = (): string => {
  try { return apiService.getStoredAccessToken?.() || ''; } catch { return ''; }
};
const bearer = (): Record<string, string> => {
  const t = storedToken();
  return t ? { Authorization: `Bearer ${t}` } : {};
};
/** One token renewal through apiService's single-flight refresh. */
const refreshSession = async (): Promise<boolean> => {
  try {
    const fn = (apiService as any).refreshAccessToken;
    if (typeof fn !== 'function') return false;
    return !!(await fn.call(apiService));
  } catch { return false; }
};

/** "UTECH-ABC-000123-<timestamp>-<uuid>" → "UTECH-ABC-000123" (the legacy
 *  detail call does the same); anything else as it is. */
export const shortTechId = (id: string): string => {
  const s = String(id || '').trim();
  const parts = s.split('-');
  return s.startsWith('UTECH-') && parts.length > 3 ? parts.slice(0, 3).join('-') : s;
};

const errText = (body: any, status: number): string => {
  const d = body?.detail ?? body?.error;
  if (typeof d === 'string' && d) return d;
  if (d && typeof d === 'object') return String(d.message || d.error || `Request failed (${status}).`);
  if (status === 429) return 'Too many requests — please wait a moment and try again.';
  return `Request failed (${status}).`;
};

// ---------------------------------------------------------------------------
// GET /api/v1/tech-cards/{id}
// ---------------------------------------------------------------------------

export interface CardResult { ok: boolean; data: TechCardPayload | null; status: number; error: string }

const TTL_MS = 5 * 60 * 1000;
const cardCache = new Map<string, { at: number; uid: string; data: TechCardPayload }>();
const inFlight = new Map<string, Promise<CardResult>>();

export const peekTechCard = (id: string): TechCardPayload | null => {
  const hit = cardCache.get(shortTechId(id));
  return hit && hit.uid === storedUserId() && Date.now() - hit.at < TTL_MS ? hit.data : null;
};

export const invalidateTechCard = (id: string): void => { cardCache.delete(shortTechId(id)); };

/** Drop every cached card — the viewer's plan changed (upgrade, trial start),
 *  so what each card may show changed too. */
export const clearTechCardCache = (): void => { cardCache.clear(); };

/** Keep a cached payload in step with a change made here (a bookmark). */
export const patchCachedTechCard = (id: string, patch: Partial<TechCardPayload>): void => {
  const key = shortTechId(id);
  const hit = cardCache.get(key);
  if (hit) cardCache.set(key, { ...hit, data: { ...hit.data, ...patch } });
};

async function getCardOnce(key: string): Promise<{ status: number; body: any }> {
  const uid = storedUserId();
  const qs = uid ? `?user_id=${encodeURIComponent(uid)}` : '';
  const res = await fetch(`${API()}/api/v1/tech-cards/${encodeURIComponent(key)}${qs}`, {
    headers: { Accept: 'application/json', ...(uid ? bearer() : {}) },
    mode: 'cors',
    credentials: 'omit',
  });
  const body = await res.json().catch(() => ({} as any));
  return { status: res.status, body };
}

export function fetchTechCard(id: string, opts: { force?: boolean } = {}): Promise<CardResult> {
  const key = shortTechId(id);
  if (!key) return Promise.resolve({ ok: false, data: null, status: 400, error: 'No technology id.' });
  if (!opts.force) {
    const hit = peekTechCard(key);
    if (hit) return Promise.resolve({ ok: true, data: hit, status: 200, error: '' });
  }
  const running = inFlight.get(key);
  if (running) return running;
  const p = (async (): Promise<CardResult> => {
    try {
      let { status, body } = await getCardOnce(key);
      // An expired access token: renew once and ask again, so a signed-in
      // viewer is not shown the signed-out card.
      if (status === 401 && storedUserId() && await refreshSession()) {
        ({ status, body } = await getCardOnce(key));
      }
      const payload = body && typeof body === 'object' && body.data && typeof body.data === 'object' && body.data.id
        ? body.data : body;
      if (status >= 200 && status < 300 && payload && payload.id) {
        const data = payload as TechCardPayload;
        cardCache.set(key, { at: Date.now(), uid: storedUserId(), data });
        return { ok: true, data, status, error: '' };
      }
      return {
        ok: false, data: null, status,
        error: status === 404 ? 'This technology is no longer listed.' : errText(body, status),
      };
    } catch {
      return { ok: false, data: null, status: 0, error: 'Network error — check your connection and try again.' };
    } finally {
      inFlight.delete(key);
    }
  })();
  inFlight.set(key, p);
  return p;
}

// ---------------------------------------------------------------------------
// GET /api/v1/ui-config → which card design the platform shows
// ---------------------------------------------------------------------------

const DESIGN_KEY = 'utech_tech_card_design';
let designValue: TechCardDesign | null = null;
let designPromise: Promise<TechCardDesign> | null = null;
const designListeners = new Set<(d: TechCardDesign) => void>();

const lastKnownDesign = (): TechCardDesign | null => {
  try {
    const v = localStorage.getItem(DESIGN_KEY);
    return v === 'accordion' || v === 'legacy' ? v : null;
  } catch { return null; }
};

/**
 * The admin's switch. A failed lookup means the server predates the switch
 * (or is unreachable): the legacy card then works on the old endpoints, so
 * that is the fallback. Only a real answer is remembered for the next load.
 */
export function loadTechCardDesign(): Promise<TechCardDesign> {
  if (designValue) return Promise.resolve(designValue);
  if (designPromise) return designPromise;
  designPromise = fetch(`${API()}/api/v1/ui-config`, { headers: { Accept: 'application/json' }, mode: 'cors', credentials: 'omit' })
    .then(async r => {
      const b = await r.json().catch(() => ({} as any));
      const v = (b?.data && typeof b.data === 'object' ? b.data : b)?.tech_card_design;
      if (!r.ok || (v !== 'accordion' && v !== 'legacy')) return 'legacy' as TechCardDesign;
      try { localStorage.setItem(DESIGN_KEY, v); } catch { /* storage off */ }
      return v as TechCardDesign;
    })
    .catch(() => 'legacy' as TechCardDesign)
    .then(v => {
      designValue = v;
      designListeners.forEach(l => l(v));
      return v;
    });
  return designPromise;
}

// ---------------------------------------------------------------------------
// POST /api/v1/share-links
// ---------------------------------------------------------------------------

export type ShareChannel = 'copy' | 'linkedin' | 'whatsapp' | 'x' | 'email' | 'native';
/** What a share link points at: a technology, a university, an OCEAN problem
 *  / company card, or a shared OCEAN collection (its share token) (Backend share_links.TARGET_KINDS). */
export type ShareTargetKind = 'tech' | 'university' | 'ocean_problem' | 'ocean_company' | 'ocean_collection';
export type ShareSurface = 'platform' | 'techpage' | 'unipage';

export interface ShareLink { code: string; url: string }

/** One code per sharer per target per surface (the server reuses it), so a
 *  link fetched once is kept for the tab: later shares act at once and the
 *  event is recorded in the background. */
const shareCache = new Map<string, ShareLink>();
const shareKey = (kind: string, id: string, surface: string) => `${storedUserId()}|${surface}|${kind}|${id}`;

export const cachedShareLink = (kind: ShareTargetKind, id: string, surface: ShareSurface): ShareLink | null =>
  shareCache.get(shareKey(kind, id, surface)) || null;

/** The same link, labelled for another channel (utm_medium). */
export const linkForChannel = (url: string, channel: ShareChannel): string => {
  try {
    const u = new URL(url);
    u.searchParams.set('utm_medium', channel);
    return u.toString();
  } catch { return url; }
};

export async function createShareLink(body: {
  target_kind: ShareTargetKind;
  target_id: string;
  surface: ShareSurface;
  channel: ShareChannel;
}): Promise<ShareLink | null> {
  const uid = storedUserId();
  const qs = uid ? `?user_id=${encodeURIComponent(uid)}` : '';
  let ids: { visitor_id?: string; session_id?: string } = {};
  try { ids = currentIds(); } catch { /* tracking off */ }
  try {
    const res = await fetch(`${API()}/api/v1/share-links${qs}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', ...(uid ? bearer() : {}) },
      body: JSON.stringify({ ...body, ...ids }),
      mode: 'cors',
      credentials: 'omit',
    });
    const b = await res.json().catch(() => ({} as any));
    const d = b?.data && typeof b.data === 'object' && b.data.url ? b.data : b;
    if (!res.ok || !d?.url) return null;
    const link = { code: String(d.code || ''), url: String(d.url) };
    shareCache.set(shareKey(body.target_kind, body.target_id, body.surface), link);
    return link;
  } catch { return null; }
}

// ---------------------------------------------------------------------------
// Small helpers the card and the lists share
// ---------------------------------------------------------------------------

/** "TRL 6" from a 1–9 value in any of the shapes search metadata uses, else "TRL —". */
export const trlPillOf = (v: unknown): string => {
  const m = String(v ?? '').match(/\b([1-9])\b/);
  return m ? `TRL ${m[1]}` : 'TRL —';
};

export const onDesignChange = (fn: (d: TechCardDesign) => void): (() => void) => {
  designListeners.add(fn);
  return () => { designListeners.delete(fn); };
};

export const currentDesign = (): TechCardDesign | null => designValue;
export const initialDesign = (): TechCardDesign => designValue || lastKnownDesign() || 'accordion';
